// ============================================================
// 用户 / 认证 API
// ============================================================

import { get, post, put } from '../client'
import type { ApiResponse } from '@task-bazaar/shared'

// ─── 登录 / 注册 ───────────────────────────────────────────

export interface RegisterParams {
  username: string
  password: string
  inviteCode?: string
  captchaToken: string
  captchaAnswer: string
  deviceFingerprint?: string
}

export interface LoginParams {
  username: string
  password: string
}

export interface AuthResult {
  accessToken: string
  refreshToken: string
  user: {
    id: string
    username: string
    nickname: string
    avatar: string
    phone?: string
    role?: string
    balance: number
    frozenBalance?: number
    reputation: number
    totalPublished?: number
    totalCompleted?: number
    inviteCode: string
    membershipType?: string
    createdAt: string
  }
}

export function register(params: RegisterParams) {
  return post<ApiResponse<AuthResult>>('/auth/register', params)
}

export function login(params: LoginParams) {
  return post<ApiResponse<AuthResult>>('/auth/login', params)
}

export function refreshToken(refreshToken: string) {
  return post<ApiResponse<{ accessToken: string; refreshToken: string }>>('/auth/refresh', { refreshToken })
}

// ─── 用户信息 ───────────────────────────────────────────────

export interface UserProfile {
  id: string
  username: string
  nickname: string
  avatar: string
  phone: string | null
  balance: number
  frozenBalance: number
  reputation: number
  totalPublished: number
  totalCompleted: number
  inviteCode: string
  membershipType: string
  membershipExpireAt: string | null
  consecutiveSignInDays: number
  createdAt: string
}

export function getProfile() {
  return get<ApiResponse<UserProfile>>('/auth/profile')
}

export function updateProfile(data: { nickname?: string; avatar?: string }) {
  return put<ApiResponse<UserProfile>>('/auth/profile', data)
}

export function bindPhone(phone: string, code: string, captchaToken: string, captchaAnswer: string) {
  return post<ApiResponse<{ success: boolean }>>('/auth/bind-phone', { phone, code, captchaToken, captchaAnswer })
}

export function changePassword(oldPassword: string, newPassword: string, captchaToken: string, captchaAnswer: string) {
  return post<ApiResponse<{ success: boolean; message: string }>>('/auth/change-password', { oldPassword, newPassword, captchaToken, captchaAnswer })
}

export interface InviteStats {
  inviteCount: number
  maxInvites: number
  remaining: number
  recentInvites: Array<{
    nickname: string
    avatar: string
    reward: number
    createdAt: string
  }>
  total: number
  page: number
  pageSize: number
}

export function getInviteStats(page?: number) {
  return get<ApiResponse<InviteStats>>('/auth/invite-stats', { params: { page } })
}
